import { listCountries } from '@shared/normalize/location';
import { QUICK_START_IDEAS, type QuickStartResponse } from '@shared/quickstart';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowRight, Search, Sparkles } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button } from '../../components/ui/button';
import { Card, CardBody, CardHeader } from '../../components/ui/card';
import { Alert, Spinner } from '../../components/ui/feedback';
import { Field, Input, Select } from '../../components/ui/form';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/query';
import { errorMessage, useMe } from '../../lib/session';

interface ReadyResponse {
  ready: Array<{ id: string; label: string }>;
  skipped: Array<{ id: string; label: string; reason: string }>;
  canRun: boolean;
}

const countries = listCountries();

/**
 * The shortest path from "I want a list of X in Y" to a running research project.
 *
 * Two boxes and a country. Everything else — which sources can run, which attributes to
 * collect, how to merge and filter — is decided server-side from the same defaults the wizard
 * would have offered, so a first-time user never has to meet those concepts to get results.
 * The wizard is still one click away for anyone who wants to control them.
 */
export function QuickStart() {
  const me = useMe();
  const navigate = useNavigate();

  const [what, setWhat] = useState('');
  const [where, setWhere] = useState('');
  const [country, setCountry] = useState(() => me.data?.workspace.settings.defaults?.country ?? 'EG');

  const ready = useQuery({
    queryKey: queryKeys.quickStartReady,
    queryFn: () => api.get<ReadyResponse>('/api/quick-start/ready'),
  });

  const start = useMutation({
    mutationFn: () =>
      api.post<QuickStartResponse>('/api/quick-start', {
        what: what.trim(),
        where: where.trim(),
        country,
        start: true,
      }),
    onSuccess: (result) => {
      void navigate(`/research/${result.projectId}`);
    },
  });

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!what.trim() || !where.trim()) return;
    start.mutate();
  };

  const blocked = ready.data?.canRun === false;

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent" aria-hidden="true" />
            Find businesses
          </span>
        }
        description="Say what you are looking for and where. Everything else is set up for you."
        action={
          <Link to="/research/new">
            <Button size="sm" variant="ghost">
              Advanced setup
            </Button>
          </Link>
        }
      />
      <CardBody>
        {blocked ? (
          <Alert className="mb-3" tone="warning" title="No source is connected yet">
            {ready.data?.skipped[0]?.reason ?? 'Connect a source before running research.'}{' '}
            <Link className="font-medium text-accent underline-offset-4 hover:underline" to="/sources">
              Open the Sources page
            </Link>
            .
          </Alert>
        ) : null}

        {start.error ? (
          <Alert className="mb-3" tone="error">
            {errorMessage(start.error)}
          </Alert>
        ) : null}

        <form className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,180px)_auto] sm:items-end" onSubmit={onSubmit}>
          <Field label="What are you looking for?">
            {({ id }) => (
              <Input
                id={id}
                value={what}
                placeholder="dentists"
                autoComplete="off"
                onChange={(event) => setWhat(event.target.value)}
              />
            )}
          </Field>

          <Field label="Where?">
            {({ id }) => (
              <Input
                id={id}
                value={where}
                placeholder="Alexandria"
                autoComplete="off"
                onChange={(event) => setWhere(event.target.value)}
              />
            )}
          </Field>

          <Field label="Country">
            {({ id }) => (
              <Select id={id} value={country} onChange={(event) => setCountry(event.target.value)}>
                {countries.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Button
            type="submit"
            className="h-9"
            icon={<ArrowRight className="h-3.5 w-3.5" />}
            loading={start.isPending}
            disabled={!what.trim() || !where.trim() || blocked}
          >
            Start
          </Button>
        </form>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="flex items-center gap-1 text-[13px] text-muted-foreground">
            <Search className="h-3 w-3" aria-hidden="true" />
            Try:
          </span>
          {QUICK_START_IDEAS.map((idea) => (
            <button
              key={idea.what}
              type="button"
              className="rounded-full border border-border px-2.5 py-0.5 text-[12px] text-muted-foreground transition-colors duration-150 hover:border-accent hover:text-accent"
              onClick={() => setWhat(idea.what)}
            >
              {idea.label}
            </button>
          ))}
        </div>

        {ready.isPending ? (
          <p className="mt-3 flex items-center gap-2 text-[13px] text-muted-foreground">
            <Spinner className="h-3 w-3" /> Checking which sources are ready…
          </p>
        ) : ready.data && ready.data.ready.length > 0 ? (
          <p className="mt-3 text-[13px] text-muted-foreground">
            Will search {ready.data.ready.map((entry) => entry.label).join(' and ')}. Results are what these sources
            surfaced for your query — a market observation, not a complete census.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
